<template>
    <v-container>
      <v-row v-show="!speakerView" class="mb-3">
        <!-- Výběr roku -->
        <v-col cols="12" md="2" :offset-md="showOtp ? 0 : 3" class="order-1 order-md-3">
            <SelectYear v-model="selectedYear" />
        </v-col>

        <!-- OTP -->
        <v-col cols="12" md="3" class="order-3 order-md-2 pa-0" v-if="showOtp">
          <v-otp-input
            v-model="otpCode"
            length="6"
            focus-all
            type="text"
            class="pa-0"
            :error="otpError"
            hide-details
          />
          <div class="text-caption text-center">
            {{ $t('live_results.otp_help') }}
          </div>
        </v-col>

        <!-- Výběr závodu -->
        <v-col cols="12" md="7" class="order-2 order-md-1">
          <v-select
            :items="raceOptions"
            v-model="selectedRace"
            density="comfortable"
            :label="$t('live_results.choose_race')"
            hide-details
            item-title="label"
            item-value="value"
            :loading="loading"
            loading-text="general.loading"
          >
            <template #item="{ props, item }">
              <v-list-item v-bind="props" :class="item.raw.value?.user_has_entry ? 'has-entry' : ''">
                <template #title>
                  {{ formatShortDate(item.raw.value.race_date) }} -
                  <v-icon v-if="item.raw.isNational" class="mb-1" size="16">mdi-trophy</v-icon>
                  {{ item.raw.value.race_name }}  ({{ item.raw.value.event_name }})
                </template>
              </v-list-item>
            </template>

            <template #selection="{ item }">
              <span class="text-truncate">
                {{ formatShortDate(item.raw.value.race_date) }} -
                <v-icon v-if="item.raw.isNational" class="mb-1" size="16">mdi-trophy</v-icon>
                {{ item.raw.value.race_name }}  ({{ item.raw.value.event_name }})
              </span>
            </template>
          </v-select>
        </v-col>
      </v-row>
      <!-- Tabulka výsledků -->
      <ResultsTable 
        v-if="selectedRace && !speakerView" 
        :raceId="selectedRace.id" 
        :code="otpCode" 
        :otpAttemptId="otpAttemptId"
        @restrict-change="onRestrictChange"
        @open-speaker-view="openSpeakerView"
      />
      <ResultsTableSpeaker
        v-else-if="selectedRace && speakerView"
        :raceId="selectedRace.id"
        :code="otpCode"
        :otpAttemptId="otpAttemptId"
        @restrict-change="onRestrictChange"
        @exit-speaker-view="exitSpeakerView"
      />
    </v-container>
  </template>
  
<script setup>
  import { ref, onMounted, watch, computed } from 'vue';
  import { useRoute, useRouter } from 'vue-router';
  import ResultsTable from '@/components/shared/ResultsTable.vue';
  import ResultsTableSpeaker from '@/components/shared/ResultsTableSpeaker.vue';
  import SelectYear from '@/components/shared/SelectYear.vue';
  import apiClient from '/api.js';
  import { useDateTimeFormat } from '@/composables/useDateTimeFormat';


  const { formatShortDate } = useDateTimeFormat();
  const route = useRoute();
  const router = useRouter();

  const speakerView = computed(() => route.name === 'ResultsPageSpeaker');

  const routeYear = computed(() => {
    const y = Number(route.params.year);
    return Number.isFinite(y) && y > 1900 ? y : null;
  });

  const routeRaceId = computed(() => {
    const id = Number(route.params.raceId);
    return Number.isFinite(id) && id > 0 ? id : null;
  });

  const selectedYear = ref(null); // Vybraný rok
  const races = ref([]); // Načtené závody
  const selectedRace = ref(null); // Vybraný závod
  const loading = ref(false)
  let syncingFromRoute = false

  const resultsRestrict = ref(false)
  const otpError = ref(false)
  const otpCode = ref('')

  // Možnosti závodů pro select
  const raceOptions = ref([]);
  
  // kód, který se pošle do ResultsTable (jen když je 6 znaků)
  const otpRequestCode = ref('')

  // identifikátor pokusu – aby child věděl, že jde o OTP request
  const otpAttemptId = ref(0)

  const showOtp = computed(() => {
    if (!resultsRestrict.value) return false
    if (!selectedRace.value?.race_date) return false
    const today = new Date().toISOString().slice(0, 10)
    return selectedRace.value.race_date === today
  })

  const navigateToRace = (year, race, { speaker = false, replace = false } = {}) => {
    if (!year || !race?.id) {
      router.replace({ name: 'ResultsPage' });
      return;
    }
    const target = {
      name: speaker ? 'ResultsPageSpeaker' : 'ResultsPageRace',
      params: {
        year: String(year),
        raceId: String(race.id),
      },
    };
    if (replace) router.replace(target);
    else router.push(target);
  };

  const openSpeakerView = () => {
    if (!selectedRace.value || !selectedYear.value) return;
    navigateToRace(selectedYear.value, selectedRace.value, { speaker: true });
  };

  const exitSpeakerView = () => {
    if (!selectedRace.value || !selectedYear.value) return;
    navigateToRace(selectedYear.value, selectedRace.value, { speaker: false });
  };

  const pickDefaultRace = () => {
    if (!raceOptions.value.length) return null;
    const today = new Date().toISOString().split('T')[0];
    const racesToday = raceOptions.value.filter(
      (option) => option.value.race_date === today
    );
    if (racesToday.length > 0) return racesToday[0].value;
    return raceOptions.value[0].value;
  };

  const urlMatches = (year, race, speaker) => {
    if (!year || !race?.id) return route.name === 'ResultsPage';
    const expectedName = speaker ? 'ResultsPageSpeaker' : 'ResultsPageRace';
    return (
      route.name === expectedName
      && String(route.params.year) === String(year)
      && String(route.params.raceId) === String(race.id)
    );
  };

  const applyRaceFromRouteOrDefault = () => {
    const fromRoute = routeRaceId.value
      ? races.value.find((race) => race.id === routeRaceId.value)
      : null;
    // pokud URL nemá raceId (nebo je neplatné) → původní výchozí logika
    const nextRace = fromRoute || pickDefaultRace();

    syncingFromRoute = true;
    selectedRace.value = nextRace;
    syncingFromRoute = false;

    if (!nextRace || !selectedYear.value) {
      if (route.name !== 'ResultsPage') {
        router.replace({ name: 'ResultsPage' });
      }
      return;
    }

    const wantSpeaker = route.name === 'ResultsPageSpeaker' && !!fromRoute;
    if (!urlMatches(selectedYear.value, nextRace, wantSpeaker)) {
      navigateToRace(selectedYear.value, nextRace, { speaker: wantSpeaker, replace: true });
    }
  };
  
  // Funkce pro načtení závodů z API
  const fetchRaces = async (year) => {
    if (!year) return;
  
    try {
      loading.value = true
      const response = await apiClient.get('race/', {
        params: { year },
      });
      // Seřazení podle data a pak podle id
      races.value = response.data.sort((a, b) => {
        const dateComparison = new Date(b.race_date) - new Date(a.race_date);
        if (dateComparison !== 0) {
          return dateComparison;
        }
        return b.id - a.id;
      });

      // Vytvoření možností pro select závodů
      raceOptions.value = races.value.map((race) => ({
        label: `${formatShortDate(race.race_date)} - ${race.event_name} (${race.race_name})`,
        value: race,
        // bere NATIONAL z race_type nebo race_level (podle toho co posíláš)
        isNational: ((race.race_type || race.race_level) || '').toUpperCase() === 'NATIONAL',
      }));

      applyRaceFromRouteOrDefault();
    } catch (error) {
      console.error('Chyba při načítání závodů:', error);
      races.value = []; // Vyprázdní data při chybě
      raceOptions.value = []; // Vyprázdní možnosti závodů
      selectedRace.value = null; // Vyprázdní vybraný závod při chybě
    } finally {
      loading.value = false
    }
  };

  // Načtení seznamu roků a závodů při mountu komponenty
  onMounted(() => {
    // rok z URL, jinak aktuální rok (původní logika)
    selectedYear.value = routeYear.value || new Date().getFullYear();
  });


  // Načtení závodů při změně roku
  watch(selectedYear, (newYear, oldYear) => {
    if (!newYear) return;
    fetchRaces(newYear);

    // změna roku v selectu (ne ze sync z URL) → přepiš URL až po načtení závodů (v applyRace…)
    // pokud uživatel změnil rok ručně a URL měla starý rok, applyRaceFromRouteOrDefault
    // raceId z URL v novém roce obvykle nenajde → použije výchozí logiku
    if (!syncingFromRoute && oldYear != null && Number(oldYear) !== Number(newYear)) {
      // po fetchi se URL srovná; tady nic
    }
  });

  // Změna závodu v selectu → aktualizace URL
  watch(selectedRace, (race) => {
    if (syncingFromRoute || !race?.id || !selectedYear.value) return;
    if (urlMatches(selectedYear.value, race, speakerView.value)) return;
    navigateToRace(selectedYear.value, race, { speaker: speakerView.value, replace: true });
  });

  // Zpět / vpřed nebo přímý vstup na /vysledky/:year/:raceId(/speaker)
  watch(
    () => [route.name, route.params.year, route.params.raceId],
    async () => {
      const yearFromRoute = routeYear.value;

      if (yearFromRoute && Number(selectedYear.value) !== yearFromRoute) {
        syncingFromRoute = true;
        selectedYear.value = yearFromRoute; // spustí fetchRaces přes watcher
        syncingFromRoute = false;
        return;
      }

      if (!races.value.length) return;

      const fromRoute = routeRaceId.value
        ? races.value.find((race) => race.id === routeRaceId.value)
        : null;

      if (fromRoute && selectedRace.value?.id !== fromRoute.id) {
        syncingFromRoute = true;
        selectedRace.value = fromRoute;
        syncingFromRoute = false;
      } else if (!fromRoute) {
        // bez raceId v URL / neplatné id → výchozí logika + sync URL
        applyRaceFromRouteOrDefault();
      }
    }
  );

// watcher na otp posílání code a zobrazování erroru
watch(otpCode, (v) => {
    const code = (v || '').trim()
    // neposílej nic dokud nejsou 6 znaků
    if (code.length !== 6) return
    // nový pokus => smaž error (ale až teď)
    otpError.value = false
    // nastav code do requestu
    otpRequestCode.value = code
    // zvedni attempt id, child to použije k rozpoznání "OTP requestu"
    otpAttemptId.value += 1
  })


const onRestrictChange = ({ restrict, invalidCode, fromOtpAttempt }) => {
  resultsRestrict.value = !!restrict

  // error nastavuj jen pokud šlo o OTP pokus
  if (fromOtpAttempt) {
    otpError.value = !!invalidCode

    // po odpovědi OTP pokusu vždy vymaž input i requestCode
    otpCode.value = ''
    otpRequestCode.value = ''
  }
}

</script>
  

<style scoped>
.has-entry {
  background-color: rgba(34, 212, 93, 0.15);
}

/* Vuetify v-otp-input používá v-text-field; donuť zobrazení na uppercase */
:deep(.v-otp-input input) {
  text-transform: uppercase;
}

</style>
